import { useEffect, useRef, useState } from 'react';
import noUiSlider, { API } from 'nouislider';
import { NumericFormat } from 'react-number-format';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';
import useCurrencyExchange from 'src/hooks/useCurrencyExchange';
import currencies from 'src/data/currencies';
import { CONVERSION_FEE_MULTIPLIER, MasterCurrency } from 'src/lib/constants';
import useFilter from 'src/hooks/useFilter';
import { useRouter } from 'next/router';
import { Currency, PriceRangeItem } from 'src/types';
import { max } from 'lodash-es';
import { useTranslations } from 'next-intl';

type Props = {
  range: [PriceRangeItem, PriceRangeItem];
};

const PriceItem: React.FC<Props> = ({ range }) => {
  const router = useRouter();
  const translation = useTranslations('components.filter.PriceItem');
  const { setFilterParams } = useFilter();
  const currencyExchange = useCurrencyExchange();
  const currentCurrency = useCurrentCurrency();
  const [displayValue, setDisplayValue] = useState<[number | undefined, number | undefined]>([
    undefined,
    undefined,
  ]);
  const currencyObj = currencies.find((o) => o.code === currentCurrency);
  const exchangedRange = [
    Math.round(
      Number(
        currencyExchange({
          amount: { amount: range[0].masterPriceCents / 100, currency: MasterCurrency },
          toCurrency: currentCurrency,
        }).amount
      ) * (range[0].priceCurrency !== currentCurrency ? CONVERSION_FEE_MULTIPLIER : 1)
    ),
    Math.round(
      Number(
        currencyExchange({
          amount: { amount: range[1].masterPriceCents / 100, currency: MasterCurrency },
          toCurrency: currentCurrency,
        }).amount
      ) * (range[1].priceCurrency !== currentCurrency ? CONVERSION_FEE_MULTIPLIER : 1)
    ),
  ];
  const priceSliderRef = useRef<API>();
  const priceSliderEleRef = useRef<HTMLDivElement>(null);

  const updateDisplayPriceRange = (
    values: (string | number)[],
    fromCurrency: Currency,
    toCurrency: Currency
  ) => {
    const minExchangedAmount = Number(
      currencyExchange({
        amount: {
          amount: values[0],
          currency: fromCurrency,
        },
        toCurrency: toCurrency,
      }).amount
    );
    const maxExchangedAmount = Number(
      currencyExchange({
        amount: {
          amount: values[1],
          currency: fromCurrency,
        },
        toCurrency: toCurrency,
      }).amount
    );

    setDisplayValue([Number(minExchangedAmount), Number(maxExchangedAmount)]);

    return [minExchangedAmount, maxExchangedAmount];
  };

  useEffect(() => {
    if (router.isReady) {
      if (priceSliderEleRef.current) {
        if (!priceSliderRef.current) {
          priceSliderRef.current = noUiSlider.create(priceSliderEleRef.current, {
            start: [exchangedRange[0], exchangedRange[1]],
            step: 1,
            connect: true,
            range: {
              min: exchangedRange[0],
              max: exchangedRange[1],
            },
            format: {
              to: function (value) {
                return Math.floor(value);
              },
              from: function (value) {
                return Math.ceil(Number(value));
              },
            },
          });

          // range's values are already converted to currentCurrency
          updateDisplayPriceRange(exchangedRange, currentCurrency, currentCurrency);

          priceSliderRef.current.on('end', (values) => {
            const isSameAsInitial =
              Number(values[0]) === exchangedRange[0] && Number(values[1]) === exchangedRange[1];
            setFilterParams((prev) => ({
              ...prev,
              prices: isSameAsInitial ? undefined : values,
              masterPrices: isSameAsInitial
                ? undefined
                : [
                    Number(
                      currencyExchange({
                        amount: {
                          amount: values[0],
                          currency: currentCurrency,
                        },
                        toCurrency: MasterCurrency,
                      }).amount
                    ),
                    Number(
                      currencyExchange({
                        amount: {
                          amount: values[1],
                          currency: currentCurrency,
                        },
                        toCurrency: MasterCurrency,
                      }).amount
                    ),
                  ],
            }));
          });

          priceSliderRef.current.on('update', (values) => {
            // range's values are already converted to currentCurrency
            updateDisplayPriceRange(values, currentCurrency, currentCurrency);
          });

          if (
            router.query['prices[]'] &&
            router.query['prices[]'].length === 2 &&
            router.query['filter_currency']
          ) {
            const minAmount = Number(router.query['prices[]'][0]);
            const maxAmount = Number(router.query['prices[]'][1]);

            // minAmount and maxAmount's currency is from the previous filter request
            // user A uses SGD and filters prices. andd then send search url to user B.
            // user B is using currency USD. so we need to convert minAmount and maxAmount to USD
            const exchangedAmounts = updateDisplayPriceRange(
              [minAmount, maxAmount],
              router.query['filter_currency'] as Currency,
              currentCurrency
            );
            priceSliderRef.current.set(exchangedAmounts, false);
          }
        }
      }
    }
  }, [router, exchangedRange[0], exchangedRange[1], currentCurrency]);

  return (
    <>
      <div className="tw-px-4 tw-mb-5">
        <div ref={priceSliderEleRef}></div>
      </div>
      <div className="form-row tw-items-center tw-mb-4">
        <div className="col">
          <NumericFormat
            disabled={true}
            prefix={`${currencyObj?.code} `}
            allowNegative={false}
            displayType="input"
            className="form-control tw-font-sans tw-text-xs tw-px-2"
            value={displayValue[0]}
          />
        </div>
        <div className="tw-w-6 text-center">to</div>
        <div className="col">
          <NumericFormat
            disabled={true}
            prefix={`${currencyObj?.code} `}
            allowNegative={false}
            displayType="input"
            className="form-control tw-font-sans tw-text-xs tw-px-2"
            value={displayValue[1]}
          />
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-font-sans">
        <div className="tw-text-sm tw-mr-2">
          {translation('displayedIn')} {currentCurrency}
        </div>
      </div>
    </>
  );
};
export default PriceItem;
